import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';

import { useIsMobile } from '../../hooks/use-is-mobile';
import { useGetAllCampsQuery, useUpdateUserMutation } from '../../redux/services';

/**
 * Asigna campamentos a un usuario.
 *
 * Sin ningún campamento, la aplicación web es inservible: `CampSelectDialog` se abre
 * con `disableClose` y todo el menú está deshabilitado hasta que haya uno activo. Por
 * eso esto no es un detalle del alta — es parte de darle acceso a alguien.
 */
export const CampsDialog = ({ open, user, onClose }) => {
  const isMobile = useIsMobile();
  const { data: activeCamps = [], isFetching } = useGetAllCampsQuery(undefined, { skip: !open });
  const [updateUser, { isLoading: saving }] = useUpdateUserMutation();

  const [selected, setSelected] = useState(() => new Set());
  const [error, setError] = useState(null);

  const asignados = useMemo(
    () => (Array.isArray(user?.camp) ? user.camp.filter(Boolean) : []),
    [user],
  );

  useEffect(() => {
    if (!open) return;
    setSelected(new Set(asignados.map(c => String(c._id ?? c))));
    setError(null);
  }, [open, asignados]);

  /**
   * `GET /camp` solo devuelve los activos. Si el usuario tiene uno archivado, hay que
   * mezclarlo en la lista: si no, no se vería y guardar se lo quitaría sin avisar.
   */
  const camps = useMemo(() => {
    const porId = new Map(activeCamps.map(c => [String(c._id), { ...c, activo: true }]));
    for (const c of asignados) {
      const id = String(c._id ?? c);
      if (!porId.has(id)) {
        porId.set(id, { _id: id, name: c.name || 'Campamento archivado', activo: false });
      }
    }
    return [...porId.values()].sort((a, b) =>
      String(a.name).localeCompare(String(b.name), 'es', { numeric: true }),
    );
  }, [activeCamps, asignados]);

  const toggle = id =>
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const original = useMemo(() => asignados.map(c => String(c._id ?? c)).sort().join(), [asignados]);
  const sinCambios = [...selected].sort().join() === original;

  const handleSubmit = async () => {
    setError(null);
    try {
      await updateUser({ id: user._id, camp: [...selected] }).unwrap();
      onClose();
    } catch (err) {
      setError(err?.data?.message || 'No se pudieron guardar los campamentos.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ pb: 1 }}>
        Campamentos de @{user?.username}
      </DialogTitle>
      <DialogContent dividers>
        {isFetching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {selected.size === 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Sin ningún campamento no podrá usar la aplicación web: se quedará en la
                pantalla de selección de campamento.
              </Alert>
            )}
            {camps.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No hay campamentos disponibles.
              </Typography>
            ) : (
              <List dense disablePadding>
                {camps.map(camp => (
                  <ListItemButton
                    key={camp._id}
                    onClick={() => toggle(String(camp._id))}
                    dense
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox
                        edge="start"
                        size="small"
                        checked={selected.has(String(camp._id))}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText primary={camp.name} />
                    {!camp.activo && (
                      <Chip size="small" variant="outlined" label="Archivado" />
                    )}
                  </ListItemButton>
                ))}
              </List>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving || sinCambios}>
          {saving ? <CircularProgress size={20} /> : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CampsDialog;
